import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { toast } from '@/hooks/use-toast'
import { SetupPageShell } from '@/features/admin/components/SetupPageShell'
import {
  useUpdateWorkflowErpSettings,
  useWorkflowErpSettings,
} from '@/features/admin/workflowSetupQueries'
import { ERP_CREDENTIAL_FIELDS, ERP_PROVIDERS, type ErpProvider } from '@/types/admin'
import type { ApiRequestError } from '@/services/api'

/** A fixed-length placeholder, not the field's actual length — the point is
 *  just to read as "something is set here", the same way the mailbox's
 *  client secret field does. */
const SECRET_SET_PLACEHOLDER = '••••••••••••'

export function ErpSettingsPage() {
  const { organizationId, workflowId } = useParams<{ organizationId: string; workflowId: string }>()
  const navigate = useNavigate()
  const { data: settings } = useWorkflowErpSettings(organizationId, workflowId)
  const update = useUpdateWorkflowErpSettings(organizationId!, workflowId!)

  const [provider, setProvider] = useState('')
  const [safeValues, setSafeValues] = useState<Record<string, string | boolean>>({})
  const [secretValues, setSecretValues] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!settings) return
    setProvider(settings.provider)
    setSafeValues(settings.credentials?.safe ?? {})
    setSecretValues({})
  }, [settings])

  const fields = ERP_CREDENTIAL_FIELDS[provider as ErpProvider] as
    | (typeof ERP_CREDENTIAL_FIELDS)[ErpProvider]
    | undefined
  const secretIsSet = settings?.provider === provider ? (settings?.credentials?.secret_fields ?? {}) : {}

  function changeProvider(next: string) {
    setProvider(next)
    // A different provider has entirely different fields — carrying the old
    // provider's values over would just be wrong, not merely stale.
    setSafeValues({})
    setSecretValues({})
  }

  async function save() {
    try {
      await update.mutateAsync({
        provider,
        credentials: fields ? { safe: safeValues, secrets: secretValues } : undefined,
      })
      toast({ title: 'ERP settings saved' })
      navigate(`/admin/organizations/${organizationId}/workflows/${workflowId}/setup`)
    } catch (error) {
      toast({
        title: 'Could not save',
        description: (error as ApiRequestError)?.message,
        variant: 'destructive',
      })
    }
  }

  return (
    <SetupPageShell
      organizationId={organizationId}
      workflowId={workflowId}
      title="ERP connection"
      description="Which ERP the workflow writes its results to."
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Provider</label>
          <Select value={provider} onValueChange={changeProvider}>
            <SelectTrigger className="sm:w-72">
              <SelectValue placeholder="Select a provider">
                {ERP_PROVIDERS.find((p) => p.value === provider)?.label}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {ERP_PROVIDERS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {fields && (
          <div className="space-y-4 rounded-md border border-gray-100 p-4">
            {fields.safe.map((field) =>
              field.type === 'checkbox' ? (
                <label key={field.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!safeValues[field.key]}
                    onChange={(e) =>
                      setSafeValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                    }
                  />
                  {field.label}
                </label>
              ) : (
                <div key={field.key} className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">{field.label}</label>
                  <Input
                    value={(safeValues[field.key] as string) ?? ''}
                    onChange={(e) =>
                      setSafeValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                  />
                </div>
              ),
            )}

            {fields.secret.map((field) => (
              <div key={field.key} className="space-y-1">
                <label className="text-sm font-medium text-gray-700">{field.label}</label>
                <PasswordInput
                  autoComplete="new-password"
                  value={secretValues[field.key] ?? ''}
                  onChange={(e) =>
                    setSecretValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                  placeholder={secretIsSet[field.key] ? SECRET_SET_PLACEHOLDER : ''}
                  hasValue={!!secretValues[field.key]}
                />
                <p className="text-xs text-gray-400">Leave this blank to keep the current secret.</p>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-gray-400">
          The connection is not monitored yet — an ERP is only reached when an order arrives, so
          silence would not mean a fault.
        </p>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </SetupPageShell>
  )
}
